import type {TOrderIcon} from '../../../services';
import type {IFeatureFlag} from './FeatureFlag';

// TODO: rename to ISidebarLink
export interface SidebarLink {
	title: string;
	url: string;
}

export interface ICustomHTML {
	id: string; // Assigned by the client
	customerPortalRouteRegex: string;
	customerPortalRouteRegexInstance: RegExp | null; // Assigned by the client
	html: string;
}

/**
 * keys from Brand Config used for colors in App.vue as CSS properties
 */
export const BRAND_CONFIG_COLORS = [
	'actionPrimaryColor',
	'backgroundColor',
	'backgroundSecondaryColor',
	'bootScreenColor',
	'fullscreenBackgroundColor',
	'giftCardColor',
	'memberCardColor',
	'navColor',
	'pointsTextColor',
	'qrPageColor',
	'rewardClaimedScreenColor',
	'singleHighlightColor',
] as const;
type TBrandConfigColor = typeof BRAND_CONFIG_COLORS[number];

/**
 * subset of Brand Config images that may require loading via WebService.base64Image()
 */
export const BRAND_CONFIG_IMAGES_BASE64 = [
	'giftCardBackgroundImage',
	'loginBackgroundImage',
	'memberCardBackgroundImage',
] as const;
/**
 * type to use for casting when checking BRAND_CONFIG_IMAGES_BASE64.includes()
 */
export type TBrandConfigImageBase64 = typeof BRAND_CONFIG_IMAGES_BASE64[number];

/**
 * keys from Brand Config used for images in App.vue as CSS properties
 */
export const BRAND_CONFIG_IMAGES = [
	...BRAND_CONFIG_IMAGES_BASE64,
	'loyaltyInfoBackgroundImage',
	'loyaltySignupRewardImage',
	'qrPageBackgroundImage', // single, selected bg image
] as const;
type TBrandConfigImage = typeof BRAND_CONFIG_IMAGES[number];



/**
 * keys from Brand Config used for colors in App.vue as CSS properties
 */
export type TBrandConfigVariablesColor = Record<TBrandConfigColor, string>;

/**
 * keys from Brand Config used for fonts in App.vue as CSS properties
 */
interface IBrandConfigVariablesFont {
	appFont: string;
	largeTextFont: string;
}

/**
 * keys from Brand Config used for images in App.vue as CSS properties
 */
type TBrandConfigVariablesImage = Record<TBrandConfigImage, string>;

/**
 * keys from Brand Config used in App.vue as CSS properties
 */
export interface IBrandConfigVariables extends
	TBrandConfigVariablesColor,
	IBrandConfigVariablesFont,
	TBrandConfigVariablesImage
{}

export interface IBrandConfig extends IBrandConfigVariables {
	id: string;
	brandName: string;
	cardName: string;
	// This is still returned by the API, but we don't use it.
	// We will remove it from the API in the future.
	// We are leaving this here so if you notice locations is
	// returned but not set, you know why.
	// locations: ILocationLite[];
	logoImage: string;
	rewardImage: string;
	qrPageBackgroundImage: string;
	defaultOfferImage: string | null;
	backgroundImages: string[];
	drinkImages: string[];

	/**
	 * all bg images for user to choose from
	 */
	qrCardBackgroundImages: string[];
	loyaltyVisitsPerReward: number;
	loyaltyPointsPerReward: number;
	loyaltyPointsRewardName: string;
	loyaltySignupRewardOn: boolean;
	loyaltySignupRewardName: string;
	loyaltySignupRewardDaysValid: number;
	loyaltyAppInstallRewardOn: boolean;
	loyaltyAppInstallRewardName: string;
	loyaltyAppInstallRewardDescription: string;
	loyaltyAppInstallRewardImage: string | null;
	orderIcon: TOrderIcon;
	appMenuId: string;
	sidebarLinks: SidebarLink[];
	customerPortalUrl: string;
	customerPortalUrlScheme: string;
	customerPortalAppleAppStoreUrl: string;
	customerPortalGooglePlayStoreUrl: string;
	giftCardLocationId: string;
	googleMapsApiKey: string | null;
	postHogApiKey: string | null;
	postHogConfig: Record<string, any> | null;
	featureFlags: IFeatureFlag[];
	onlineStoreLink: string | null;
	pointsDisplay: string | null;
	useLineItemQuantities: boolean;
	// menu: ISimpleMenu | null;
	serviceDescriptions: Record<string, string>;
	landingFontHeadline1: string;
	landingFontHeadline2: string;
	landingFontTextBlock: string;
	landingFontLink: string;
	// customHTML: ICustomHTML[]; // Commenting out until full C deploy
	// showTestLocations: boolean;
}
